import { Button, Typography } from '@mui/material';
import ReactConfetti from 'react-confetti';
import StarBorder from '../star-border/StarBorder';
import styles from './UpgradeToPro.module.scss';
import styleVariables from '../../scss/_variables.scss';
import { observer } from 'mobx-react-lite';
import { useUpgradeRequestStore } from 'src/stores/UpgradeRequestStore/UpgradeRequestStore';
import CustomLoading from '../custom-loading';
import { useEffect, useRef, useState } from 'react';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import {
  checkUpgradeRequestButtonVisibilityBeforeFetchingUsers,
  checkUpgradeRequestButtonVisibilityAfterFetchingUsers,
} from 'src/utils/upgradeRequest';
import { useOverviewStore } from 'src/stores/OverviewStore/OverviewStore';
import { useTranslation } from 'react-i18next';
import { getHanaProTrialStatus } from 'src/utils/trial';
import {
  consumeOnboardingCelebrationPending,
  hasOnboardingCelebrationPending,
} from 'src/utils/onboarding';
import { CONFETTI_DURATION_MS } from 'src/constants/confetti';

export interface UpgradeToProProps {
  title: string;
  subTitle: string;
  ctaText: string;
  handleChangePlan: () => void;
}

const UpgradeToPro = observer(
  ({ title, subTitle, ctaText, handleChangePlan }: UpgradeToProProps) => {
    const { openUpgradeRequestPopUp, fetchAllUsersForUpgradeRequest } =
      useUpgradeRequestStore();

    const { fetchCurrentMonthUserInteractions } = useOverviewStore();

    const { getUserDetails } = useAuthStore();
    const userDetails = getUserDetails();
    const { t } = useTranslation();

    const { organizationDetails } = useOrganizationStore();
    const hanaProTrialStatus = getHanaProTrialStatus(organizationDetails);
    const isHanaProTrialActive = Boolean(hanaProTrialStatus?.isActive);
    const remainingTrialDaysLabel =
      hanaProTrialStatus?.remainingDays === 1
        ? '1 day remaining in this workspace'
        : `${hanaProTrialStatus?.remainingDays ?? 0} days remaining in this workspace`;
    const resolvedTitle = isHanaProTrialActive
      ? t('hana_pro_trial_sidebar_title', {
          defaultValue: 'Keep PRO features without interruption.',
        })
      : title;
    const resolvedSubTitle = isHanaProTrialActive
      ? t('hana_pro_trial_sidebar_subtitle', {
          defaultValue: '',
        })
      : subTitle;
    const resolvedCtaText = isHanaProTrialActive
      ? t('hana_pro_trial_sidebar_cta', {
          defaultValue: 'Upgrade Before Trial Ends',
        })
      : ctaText;

    const [loading, setLoading] = useState(true);
    const [showUpgradeRequestButton, setShowUpgradeRequestButton] =
      useState(false);
    const [showTrialCelebration, setShowTrialCelebration] = useState(false);
    const [confettiBounds, setConfettiBounds] = useState({
      width: 0,
      height: 0,
    });
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const shouldShowUpgradeRequestButton = async () => {
      setLoading(true);

      if (!userDetails || !organizationDetails) {
        setLoading(false);

        return;
      }

      const fetchUserInteractionsResponse =
        await fetchCurrentMonthUserInteractions(userDetails);

      if (!fetchUserInteractionsResponse) {
        setLoading(false);

        return;
      }

      const showUpgradeRequestButton =
        checkUpgradeRequestButtonVisibilityBeforeFetchingUsers(
          organizationDetails,
          userDetails,
          fetchUserInteractionsResponse,
        );

      if (!showUpgradeRequestButton) {
        setLoading(false);

        return;
      }

      const fetchAllUsersForUpgradeResponse =
        await fetchAllUsersForUpgradeRequest(userDetails);

      if (!fetchAllUsersForUpgradeResponse) {
        setLoading(false);

        return;
      }

      const { usersWithAdminRole, usersWithUserRole } =
        fetchAllUsersForUpgradeResponse;

      const maximumInteractionLimit =
        organizationDetails.planId.monthlyInteractionLimitPerUser;

      setShowUpgradeRequestButton(
        checkUpgradeRequestButtonVisibilityAfterFetchingUsers(
          usersWithUserRole,
          usersWithAdminRole,
          fetchUserInteractionsResponse.count,
          maximumInteractionLimit,
        ),
      );

      setLoading(false);
    };

    useEffect(() => {
      shouldShowUpgradeRequestButton();
    }, []);

    useEffect(() => {
      if (!isHanaProTrialActive) {
        return;
      }

      if (hasOnboardingCelebrationPending('trial-card')) {
        setShowTrialCelebration(true);
      }
    }, [isHanaProTrialActive]);

    useEffect(() => {
      if (!showTrialCelebration) {
        return;
      }

      const timeoutId = window.setTimeout(() => {
        consumeOnboardingCelebrationPending('trial-card');
      }, 0);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [showTrialCelebration]);

    useEffect(() => {
      if (!showTrialCelebration) {
        return;
      }

      const timeoutId = window.setTimeout(() => {
        setShowTrialCelebration(false);
      }, CONFETTI_DURATION_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [showTrialCelebration]);

    useEffect(() => {
      const wrapperElement = wrapperRef.current;

      if (!wrapperElement) {
        return;
      }

      const syncConfettiBounds = () => {
        setConfettiBounds({
          width: wrapperElement.clientWidth,
          height: wrapperElement.clientHeight,
        });
      };

      syncConfettiBounds();

      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', syncConfettiBounds);

        return () => {
          window.removeEventListener('resize', syncConfettiBounds);
        };
      }

      const resizeObserver = new ResizeObserver(syncConfettiBounds);

      resizeObserver.observe(wrapperElement);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    if (loading) {
      return <div className={styles['wrapper-card']}>{<CustomLoading />}</div>;
    }

    return (
      <div className={styles['wrapper-card']} ref={wrapperRef}>
        {showTrialCelebration &&
          confettiBounds.width > 0 &&
          confettiBounds.height > 0 && (
            <div className={styles['confetti-overlay']}>
              <ReactConfetti
                width={confettiBounds.width}
                height={confettiBounds.height}
                recycle={false}
                numberOfPieces={140}
                gravity={0.2}
                confettiSource={{
                  x: confettiBounds.width * 0.15,
                  y: 0,
                  w: confettiBounds.width * 0.7,
                  h: 12,
                }}
              />
            </div>
          )}
        {isHanaProTrialActive && (
          <div className={styles['trial-section']}>
            <Typography component="div" className={styles['trial-badge']}>
              {t('hana_pro_trial_sidebar_badge', {
                defaultValue: 'Hana PRO trial active',
              })}
            </Typography>
            <Typography component="div" className={styles['trial-caption']}>
              {t('hana_pro_trial_sidebar_remaining_days', {
                defaultValue: remainingTrialDaysLabel,
                count: hanaProTrialStatus?.remainingDays ?? 0,
              })}
            </Typography>
          </div>
        )}
        <Typography component="div" className={styles['title']}>
          {resolvedTitle}
        </Typography>
        {resolvedSubTitle ? (
          <Typography component="div" className={styles['sub-title']}>
            {resolvedSubTitle}
          </Typography>
        ) : null}
        <StarBorder className={styles['cta-wrapper']}>
          <Button
            id="upgrade-cta-btn"
            variant="contained"
            sx={{
              background: styleVariables.specialGradient,
              color: styleVariables.colorWhite,
            }}
            className={styles['cta-button']}
            onClick={handleChangePlan}
          >
            {resolvedCtaText}
          </Button>
        </StarBorder>
        {showUpgradeRequestButton && (
          <StarBorder className={styles['cta-wrapper']}>
            <Button
              id="upgrade-request-btn"
              sx={{
                marginTop: '8px',
              }}
              variant="contained"
              className={styles['cta-button']}
              onClick={openUpgradeRequestPopUp}
            >
              {t('request_to_upgrade')}
            </Button>
          </StarBorder>
        )}
      </div>
    );
  },
);

export default UpgradeToPro;
